import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Code as Key } from 'constants/keyboard';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { AccessibilityViewIndex } from 'types/accessibilityViewIndex';
import { isFiniteNumber } from 'utils/number';

import { ElementsPackTypes } from '../components/WidgetMenu/SideMenu/ElementsMenu/ElementsMenu.types';
import { ACTIVE_SIDE_MENU_PARAM } from '../components/WidgetMenu/Sidebar/Sidebar.config';
import { closeAllRightSideMenus } from './editorSettingsSlice.helpers';
import {
  ColorVisionMode,
  DownloadModalTrigger,
  EditBackgroundType,
  EditorSettingsState,
  RecommendedIconPack,
} from './editorSettingsSlice.types';

const defaultState: EditorSettingsState = {
  zoom: 1,
  isSlideView: false,
  accessibilityViewIndex: AccessibilityViewIndex.CLOSED,
  colorVisionMode: ColorVisionMode.none,
  activeWidgetMenu: WIDGET_MENU_OPTIONS.NONE,
  replaceWidgetMenuIndex: 0,
  elementsWidgetMenuIndex: null,
  isWidgetSettingsView: false,
  hasKeyboardIgnore: false,
  allowedKeyboardKeys: [],
  downloadModal: {
    isOpen: false,
    trigger: DownloadModalTrigger.Default,
  },
  originalEditorId: '',

  // Saved here due to requirement on keeping the same tab when opening settings from another data table chart widget.
  chartWidgetSettingsTabIndex: 0,

  languageUpdatedOnce: false,
  brandIdx: 0,
  keyboardShortcutModalOpen: false,

  hasShowGrid: false,
  hasShowMargin: false,
  hasShowPrintBleed: false,

  isTeamSharingEnabled: false,
  showCollaborationWarning: false,

  isUploading: false,
  editBackgroundView: undefined,

  // Set to TRUE to show alt text on canvas when widgets are selected
  showAltTagOnCanvas: false,
  hasEditedAltText: false,

  //  set to TRUE to display the gated font upload modal
  isGatedFontUploadModalOpen: false,

  //  set to TRUE to display the gated signature modal
  isGatedSignatureModalOpen: false,

  // Set to TRUE to force hide widget controls on canvas
  forceHideWidgetControl: false,

  // TRUE when user is zooming with scroll
  isScrollZooming: false,

  // if TRUE flow mode is enabled for all widgets in the editor
  isFlowModeEnabled: false,

  //  Set to TRUE to display the page number toolbar
  isPageNumberToolbarOpen: false,

  // Combined image and upload panels
  imagePanelTabIndex: 0,

  recommendedIconPack: null,

  // Enabled interaction submission
  enabledInteractionSubmission: false,
  // TRUE when the design preview modal is open
  isPreviewModalOpen: false,
  isInteractiveMenuOpen: false,
  isDragOverActive: false,
};

const getInitialState = (): EditorSettingsState => {
  const urlParams = new URLSearchParams(window.location.search);
  const sideMenu = urlParams.get(ACTIVE_SIDE_MENU_PARAM) as WIDGET_MENU_OPTIONS | null;

  if (sideMenu && Object.values(WIDGET_MENU_OPTIONS).includes(sideMenu)) {
    const url = new URL(window.location.href);
    url.searchParams.delete(ACTIVE_SIDE_MENU_PARAM);
    window.history.pushState({}, '', url);

    return {
      ...defaultState,
      activeWidgetMenu: sideMenu,
    };
  }
  return defaultState;
};

export const initialState = getInitialState();

export const editorSettings = createSlice({
  name: 'editorSettings',
  initialState,
  reducers: {
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },
    toggleSlideView: (state) => {
      const newIsSlideView = !state.isSlideView;
      if (newIsSlideView) {
        closeAllRightSideMenus(state);
      }

      state.isSlideView = newIsSlideView;
    },
    setSlideView: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeAllRightSideMenus(state);
      }

      state.isSlideView = action.payload;
    },
    setAccessibilityViewIndex: (state, action: PayloadAction<AccessibilityViewIndex>) => {
      if (action.payload !== AccessibilityViewIndex.CLOSED) {
        closeAllRightSideMenus(state);
      }

      state.accessibilityViewIndex = action.payload;
    },
    setColorVisionMode: (state, action: PayloadAction<ColorVisionMode>) => {
      state.colorVisionMode = action.payload;
    },
    dangerouslySetHasKeyboardIgnore: (
      state,
      action: PayloadAction<{ hasKeyboardIgnore: boolean; allowedKeyboardKeys?: Key[] }>,
    ) => {
      const { hasKeyboardIgnore, allowedKeyboardKeys } = action.payload;
      state.hasKeyboardIgnore = hasKeyboardIgnore;
      state.allowedKeyboardKeys = allowedKeyboardKeys || [];
    },
    setDownloadModal: (state, action: PayloadAction<{ isOpen: boolean; trigger?: DownloadModalTrigger }>) => {
      const { isOpen, trigger = DownloadModalTrigger.Default } = action.payload;
      state.downloadModal.isOpen = isOpen;
      state.downloadModal.trigger = trigger;
    },
    setOriginalEditorId: (state, action: PayloadAction<string>) => {
      state.originalEditorId = action.payload;
    },
    setWidgetSettingsView: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeAllRightSideMenus(state);
      }

      state.isWidgetSettingsView = action.payload;
    },
    setEditBackgroundView: (state, action: PayloadAction<EditBackgroundType>) => {
      const editBackgroundType = action.payload;
      // Close any open side panels
      if (editBackgroundType !== undefined) {
        closeAllRightSideMenus(state);
      }

      state.editBackgroundView = editBackgroundType;
    },
    setActiveWidgetMenu: (state, action: PayloadAction<WIDGET_MENU_OPTIONS>) => {
      if (action.payload === state.activeWidgetMenu) state.activeWidgetMenu = WIDGET_MENU_OPTIONS.NONE;
      else state.activeWidgetMenu = action.payload;

      // reset the replace widget menu index
      if (state.activeWidgetMenu === WIDGET_MENU_OPTIONS.NONE && state.replaceWidgetMenuIndex !== 0) {
        state.replaceWidgetMenuIndex = 0;
      }
    },
    setActiveElementWidgetMenu: (state, action: PayloadAction<ElementsPackTypes | null>) => {
      if (action.payload === state.elementsWidgetMenuIndex) state.elementsWidgetMenuIndex = null;
      else state.elementsWidgetMenuIndex = action.payload;
    },
    setChartSettingsTabIndex: (state, action: PayloadAction<{ tabIndex: number }>) => {
      const { tabIndex } = action.payload;
      state.chartWidgetSettingsTabIndex = tabIndex;
    },
    setLanguageUpdatedOnce: (state, action: PayloadAction<boolean>) => {
      state.languageUpdatedOnce = action.payload;
    },
    setBrandIdx: (state, action: PayloadAction<number>) => {
      state.brandIdx = isFiniteNumber(action.payload) ? action.payload : 0;
    },
    setKeyboardShortcutModalOpen: (state, action: PayloadAction<boolean>) => {
      state.keyboardShortcutModalOpen = action.payload;
    },
    toggleShowGrid: (state) => {
      state.hasShowGrid = !state.hasShowGrid;
    },
    toggleShowMargin: (state) => {
      state.hasShowMargin = !state.hasShowMargin;
    },
    toggleShowPrintBleed: (state) => {
      state.hasShowPrintBleed = !state.hasShowPrintBleed;
    },
    setTeamSharingEnabled: (state, action: PayloadAction<boolean>) => {
      state.isTeamSharingEnabled = action.payload;
    },
    setShowCollaborationWarning: (state, action: PayloadAction<boolean>) => {
      state.showCollaborationWarning = action.payload;
    },
    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
    setReplaceWidgetMenuIndex: (state, action: PayloadAction<number>) => {
      state.replaceWidgetMenuIndex = action.payload;
    },
    setShowAltTagOnCanvas(state, action: PayloadAction<boolean>) {
      state.showAltTagOnCanvas = action.payload;
    },
    setHasEditedAltText(state, action: PayloadAction<boolean>) {
      state.hasEditedAltText = action.payload;
    },
    setIsGatedFontUploadModalOpen(state, action: PayloadAction<boolean>) {
      state.isGatedFontUploadModalOpen = action.payload;
    },
    setIsGatedSignatureModalOpen(state, action: PayloadAction<boolean>) {
      state.isGatedSignatureModalOpen = action.payload;
    },
    setForceHideWidgetControl(state, action: PayloadAction<boolean>) {
      state.forceHideWidgetControl = action.payload;
    },
    setIsScrollZooming(state, action: PayloadAction<boolean>) {
      state.isScrollZooming = action.payload;
    },
    setIsFlowModeEnabled(state, action: PayloadAction<boolean>) {
      state.isFlowModeEnabled = action.payload;
    },
    setIsPageNumberToolbarOpen(state, action: PayloadAction<boolean>) {
      state.isPageNumberToolbarOpen = action.payload;
    },
    setImagePanelTabIndex(state, action: PayloadAction<number>) {
      state.imagePanelTabIndex = action.payload;
    },
    setRecommendedIconPack(state, action: PayloadAction<RecommendedIconPack | null>) {
      state.recommendedIconPack = action.payload;
    },
    setEnabledInteractionSubmission(state, action: PayloadAction<boolean>) {
      state.enabledInteractionSubmission = action.payload;
    },
    setIsPreviewModalOpen(state, action: PayloadAction<boolean>) {
      state.isPreviewModalOpen = action.payload;
    },
    setIsInteractiveMenuOpen(state, action: PayloadAction<boolean>) {
      state.isInteractiveMenuOpen = action.payload;
    },
    setIsDragOverActive(state, action: PayloadAction<boolean>) {
      state.isDragOverActive = action.payload;
    },
  },
});

export const {
  setZoom,
  toggleSlideView,
  setAccessibilityViewIndex,
  setColorVisionMode,
  dangerouslySetHasKeyboardIgnore,
  setDownloadModal,
  setOriginalEditorId,
  setWidgetSettingsView,
  setChartSettingsTabIndex,
  setLanguageUpdatedOnce,
  setActiveWidgetMenu,
  setActiveElementWidgetMenu,
  setBrandIdx,
  setSlideView,
  setKeyboardShortcutModalOpen,
  toggleShowGrid,
  toggleShowPrintBleed,
  setTeamSharingEnabled,
  setShowCollaborationWarning,
  setIsUploading,
  setEditBackgroundView,
  toggleShowMargin,
  setReplaceWidgetMenuIndex,
  setShowAltTagOnCanvas,
  setHasEditedAltText,
  setIsGatedFontUploadModalOpen,
  setIsGatedSignatureModalOpen,
  setForceHideWidgetControl,
  setIsScrollZooming,
  setIsFlowModeEnabled,
  setIsPageNumberToolbarOpen,
  setImagePanelTabIndex,
  setRecommendedIconPack,
  setEnabledInteractionSubmission,
  setIsPreviewModalOpen,
  setIsInteractiveMenuOpen,
  setIsDragOverActive,
} = editorSettings.actions;

export default editorSettings.reducer;
